import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CustomMarker,
  DateHeader,
  IntervalRenderer,
  ReactCalendarTimelineProps,
  SidebarHeader,
  TimelineHeaders,
  TimelineItemBase,
  TimelineMarkers,
  Timeline as TimelineWithFields,
} from "react-calendar-timeline";

import moment from "moment";

import {
  formatLocalizedMonth,
  getLanguageFromLocalStorage,
  getLocalStorageLastViewedYear,
  getVerticalLineClassNamesForTime,
  useGetFarmReportingYear,
  useGetFarms,
  useStickyTimelineScrollbar,
  useTranslation,
} from "utils";

import { CarbonProgramOutcomesResponse, FieldListField } from "models";

import { EmptyStateCard, FieldsCountSummary, Flex, Tag } from "components";

import { FieldWithEnrollmentData } from "pages/app/all-field-view/components/AllFieldsTimeline";
import { getTimelineDefaultStartAndEnd } from "pages/app/field-timeline/utils/TimelineUtils";

export type FieldRowHighlight = { groupId: string | number; startTime: number; endTime: number; classNames?: string[] };

// Single source of truth for row/item sizing: both the RCT props below (lineHeight, itemHeightRatio)
// and the CSS (--rct-row-height / --rct-item-height, consumed by timeline-calendar-with-fields.scss to
// flush items to the top of their row) derive from these two values, so changing the row height only
// means changing TIMELINE_ROW_HEIGHT_PX here.
export const TIMELINE_ROW_HEIGHT_PX = 90;
const TIMELINE_ITEM_HEIGHT_PX = 45;

// With crop details shown, each main crop card gets a details panel underneath it (up to 4 label/value lines),
// so side crops are pushed down past that panel and the row grows to fit both.
const CROP_DETAILS_PANEL_GAP_PX = 4;
const CROP_DETAILS_PANEL_MAX_HEIGHT_PX = 160;
const CROP_DETAILS_SIDE_CROP_OFFSET_PX = TIMELINE_ITEM_HEIGHT_PX + CROP_DETAILS_PANEL_GAP_PX + CROP_DETAILS_PANEL_MAX_HEIGHT_PX + CROP_DETAILS_PANEL_GAP_PX;
const TIMELINE_ROW_WITH_CROP_DETAILS_HEIGHT_PX = CROP_DETAILS_SIDE_CROP_OFFSET_PX + TIMELINE_ITEM_HEIGHT_PX;

export const getTimelineRowHeight = (showCropDetails: boolean) => (showCropDetails ? TIMELINE_ROW_WITH_CROP_DETAILS_HEIGHT_PX : TIMELINE_ROW_HEIGHT_PX);

// Single source of truth for the sidebar/field-cell width: both the sidebarWidth prop below and the CSS
// (--rct-sidebar-width, consumed by timeline-calendar-with-fields.scss for the sticky scrollbar and header
// alignment) derive from this value, so changing the sidebar width only means changing this constant.
export const TIMELINE_SIDEBAR_WIDTH_PX = 332;

// Reports a CustomMarker's own `styles.left` (computed by RCT itself for that exact date) up to state via
// this component's own effect, rather than calling the setter directly from the marker's render prop —
// that would mean updating state while a different component is still rendering.
const MarkerLeftReporter = ({ left, onChange }: { left: number; onChange: (left: number) => void }) => {
  useEffect(() => {
    onChange(left);
  }, [left, onChange]);

  return null;
};

export type TimelineCalendarWithFieldsProps = ReactCalendarTimelineProps<TimelineItemBase<number>, FieldWithEnrollmentData> & {
  className?: string;
  allFields: FieldListField[];
  topHeaderExtras?: React.ReactNode;
  highlightCurrentYear?: boolean;
  rowHighlights?: FieldRowHighlight[];
  tagText?: string;
  screenSize?: "small" | "medium" | "large";
  farmFieldsCount?: number;
  enrolledFieldsCount?: number;
  enrolledFieldsTotalHectares?: number;
  isEnrollmentDataLoading?: boolean;
  fieldsSizeSummary?: string | null;
  hasAppliedFilters?: boolean;
  carbonProgramOutcomesResponse?: CarbonProgramOutcomesResponse;
  reportingYear: number;
  onShowFieldUpload?: () => void;
  showCropDetails?: boolean;
};
const TimelineCalendarWithFieldsComponent = <T,>({
  className = "",
  highlightCurrentYear,
  rowHighlights,
  topHeaderExtras,
  tagText,
  allFields,
  screenSize,
  farmFieldsCount,
  enrolledFieldsCount,
  enrolledFieldsTotalHectares,
  isEnrollmentDataLoading,
  fieldsSizeSummary,
  hasAppliedFilters,
  reportingYear,
  onShowFieldUpload,
  showCropDetails = false,
  scrollRef: externalScrollRef,
  ...props
}: TimelineCalendarWithFieldsProps) => {
  const rowHeight = getTimelineRowHeight(showCropDetails);
  const { t } = useTranslation(["allFieldView"]);
  const { currentFarm } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram ?? null;
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);

  const language = getLanguageFromLocalStorage({ forDates: true });

  // set the language
  moment.locale(language);

  const firstEntryYear = Math.min(...props.items.filter((item) => item.start_time).map((item) => moment(item.start_time).year()));
  const startLimit = moment(`${firstEntryYear}-01-01`).subtract(3, "years").startOf("year").valueOf();
  const endLimit = moment().add(5, "years").endOf("year").valueOf();

  const lastViewedYear = currentFarm?.id ? getLocalStorageLastViewedYear(currentFarm.id) : undefined;
  const effectiveReportingYear = lastViewedYear ?? activeFarmReportingYear;

  const timelineStartEnd = useMemo(
    () => getTimelineDefaultStartAndEnd(effectiveReportingYear, carbonProgram, screenSize),
    [effectiveReportingYear, carbonProgram, screenSize]
  );
  const timelineZoomLevel = timelineStartEnd.end - timelineStartEnd.start;

  const { stickyScrollbarRef, stickyScrollbarThumbRef, scrollRef, syncThumb } = useStickyTimelineScrollbar({
    timeStart: startLimit,
    timeEnd: endLimit,
    initialVisibleTimeStart: timelineStartEnd.start,
    initialVisibleTimeEnd: timelineStartEnd.end,
  });

  const combinedScrollRef = useCallback(
    (el: HTMLElement | null) => {
      scrollRef(el);
      if (typeof externalScrollRef === "function") (externalScrollRef as (el: HTMLDivElement) => void)(el as HTMLDivElement);
    },
    [scrollRef, externalScrollRef]
  );

  const wrapperRef = useRef<HTMLDivElement>(null);

  // RCT only listens to window.resize — it has no container ResizeObserver.
  // When the sidebar panel opens/closes, the container width changes without
  // a window resize event, leaving blank space. Dispatching a synthetic resize
  // event triggers RCT's internal resize handler to recalculate canvas width.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Each row highlight's right edge is positioned from its own end-time CustomMarker's `left`, keyed per
  // highlight — not extrapolated from a pixels-per-ms ratio. RCT renders many month intervals at once, each
  // independently able to report a ratio; averaged/rounded slightly differently, whichever happened to
  // report last would silently win, drifting the highlight's width run to run despite identical input dates.
  const [highlightEndLeftByKey, setHighlightEndLeftByKey] = useState<Record<string, number>>({});
  const handleHighlightEndLeftChange = useCallback((key: string, left: number) => {
    setHighlightEndLeftByKey((previous) => (previous[key] === left ? previous : { ...previous, [key]: left }));
  }, []);

  // Row heights are static (set per group in AllFieldsTimeline), so top offsets can be computed
  // once from the groups array instead of measuring the DOM.
  const rowOffsetsByGroupId = useMemo(() => {
    const offsets = new Map<string | number, { top: number; height: number }>();
    let cumulativeTop = 0;
    for (const group of props.groups) {
      const height = group.height ?? rowHeight;
      offsets.set(group.id, { top: cumulativeTop, height });
      cumulativeTop += height;
    }
    return offsets;
  }, [props.groups, rowHeight]);

  const handleTimeChange: ReactCalendarTimelineProps<TimelineItemBase<number>, FieldWithEnrollmentData>["onTimeChange"] = (
    visibleTimeStart,
    visibleTimeEnd,
    updateScrollCanvas
  ) => {
    let start = visibleTimeStart;
    let end = visibleTimeEnd;
    if (visibleTimeStart < startLimit) {
      start = startLimit;
      end = startLimit + (visibleTimeEnd - visibleTimeStart);
    } else if (visibleTimeEnd > endLimit) {
      start = endLimit - (visibleTimeEnd - visibleTimeStart);
      end = endLimit;
    }
    updateScrollCanvas(start, end);
    // Direct DOM update — no React state, no re-render on every pan event.
    syncThumb(start, end);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`timeline-calendar-sticky-wrapper ${showCropDetails ? "timeline-crop-details-active" : ""}`}
        style={
          {
            "--rct-row-height": `${rowHeight}px`,
            "--rct-item-height": `${TIMELINE_ITEM_HEIGHT_PX}px`,
            "--rct-side-crop-offset": `${showCropDetails ? CROP_DETAILS_SIDE_CROP_OFFSET_PX : TIMELINE_ITEM_HEIGHT_PX}px`,
            "--rct-crop-details-panel-max-height": `${CROP_DETAILS_PANEL_MAX_HEIGHT_PX}px`,
            "--rct-sidebar-width": `${TIMELINE_SIDEBAR_WIDTH_PX}px`,
          } as React.CSSProperties
        }
      >
        {/* eslint-disable-next-line */}
        {/* @ts-ignore */}
        <TimelineWithFields
          {...props}
          key={`${screenSize}-${effectiveReportingYear}`}
          className={`timeline-calendar timeline-calendar-with-fields ${className}`}
          lineHeight={rowHeight}
          itemHeightRatio={TIMELINE_ITEM_HEIGHT_PX / rowHeight}
          sidebarWidth={TIMELINE_SIDEBAR_WIDTH_PX}
          defaultTimeStart={timelineStartEnd.start}
          defaultTimeEnd={timelineStartEnd.end}
          // no zooming in/out allowed
          minZoom={timelineZoomLevel}
          maxZoom={timelineZoomLevel}
          verticalLineClassNamesForTime={getVerticalLineClassNamesForTime(!!highlightCurrentYear, reportingYear, carbonProgram)}
          onTimeChange={handleTimeChange}
          scrollRef={combinedScrollRef}
        >
          <TimelineHeaders className="timeline-calendar-header">
            <SidebarHeader>
              {useCallback(
                () => (
                  <Flex className="field-info-container" justify="space-between">
                    <Flex vertical gap="0.25rem">
                      <FieldsCountSummary
                        visibleFieldsCount={allFields.length}
                        totalFieldsCount={farmFieldsCount ?? 0}
                        hasFiltersActive={!!hasAppliedFilters}
                        fieldsSizeSummary={fieldsSizeSummary}
                        enrolledFieldsCount={enrolledFieldsCount}
                        enrolledFieldsTotalHectares={enrolledFieldsTotalHectares}
                        isEnrollmentDataLoading={isEnrollmentDataLoading}
                      />
                    </Flex>
                  </Flex>
                ),
                [enrolledFieldsCount, enrolledFieldsTotalHectares, isEnrollmentDataLoading, farmFieldsCount, fieldsSizeSummary, hasAppliedFilters, allFields]
              )}
            </SidebarHeader>
            <DateHeader
              unit="year"
              className="header-years"
              // eslint-disable-next-line
              // @ts-ignore
              labelFormat="YYYY"
              // eslint-disable-next-line
              // @ts-ignore
              intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
                const isCurrentYear = intervalContext.intervalText === reportingYear.toString();
                const { key, ...rest } = getIntervalProps();

                return (
                  <div className="header-years content" {...rest} key={key}>
                    <Flex align="center" justify="flex-start" gap="0.5rem">
                      {intervalContext.intervalText}
                      {isCurrentYear && tagText && <Tag className="chip-pill capitalize" colour="yellow" text={tagText} />}
                      {topHeaderExtras}
                    </Flex>
                  </div>
                );
              }}
            />
            <DateHeader
              unit="month"
              className="header-months"
              // eslint-disable-next-line
              // @ts-ignore
              labelFormat="MMMM"
              // eslint-disable-next-line
              // @ts-ignore
              intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
                const month = intervalContext.interval.startTime.month();
                const isFirstMonthOfYear = month === 0;

                const { key, ...rest } = getIntervalProps();

                return (
                  <div className={`content ${isFirstMonthOfYear && "first-month"} `} {...rest} key={key}>
                    <Flex align="center" justify="center">
                      {formatLocalizedMonth(month).slice(0, 3)}
                    </Flex>
                  </div>
                );
              }}
            />
          </TimelineHeaders>
          <TimelineMarkers>
            {rowHighlights?.flatMap((highlight) => {
              const rowOffset = rowOffsetsByGroupId.get(highlight.groupId);
              if (!rowOffset) return [];

              const key = `${highlight.groupId}-${highlight.startTime}-${highlight.endTime}`;
              const endLeft = highlightEndLeftByKey[key];

              return [
                <CustomMarker key={`${key}-end`} date={highlight.endTime}>
                  {({ styles }) => <MarkerLeftReporter left={styles.left as number} onChange={(left) => handleHighlightEndLeftChange(key, left)} />}
                </CustomMarker>,
                // Keying on endLeft forces a remount (not just a re-render) whenever it changes. CustomMarker
                // subscribes its children render-prop once at mount and keeps invoking that same closure on every
                // update — so a plain re-render would keep this marker's div sized against whatever endLeft was
                // when it first mounted, ignoring later updates as endLeft settles to its final value.
                endLeft === undefined ? null : (
                  <CustomMarker key={`${key}-start-${endLeft}`} date={highlight.startTime}>
                    {({ styles }) => (
                      <div
                        className={["rct-row-highlight", ...(highlight.classNames ?? [])].join(" ")}
                        style={{
                          ...styles,
                          bottom: "auto",
                          top: rowOffset.top,
                          height: rowOffset.height,
                          width: Math.max(0, endLeft - (styles.left as number)),
                        }}
                      />
                    )}
                  </CustomMarker>
                ),
              ];
            })}
          </TimelineMarkers>
        </TimelineWithFields>
        {allFields.length > 0 && props.items.length > 0 && (
          <div ref={stickyScrollbarRef} className="timeline-sticky-scrollbar">
            <div ref={stickyScrollbarThumbRef} className="timeline-sticky-scrollbar-thumb" />
          </div>
        )}
      </div>
      {allFields.length === 0 && !hasAppliedFilters && (
        <Flex className="timeline-empty-state" align="center" justify="center">
          <EmptyStateCard
            iconName="klim/field"
            title={t("noFields.title")}
            description={t("noFields.description")}
            buttonText={t("fieldContextMenu.addFields")}
            onButtonClick={() => onShowFieldUpload?.()}
          />
        </Flex>
      )}
    </>
  );
};

export const TimelineCalendarWithFields = memo(TimelineCalendarWithFieldsComponent);
